import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB') : '—';
const fmtDT = (iso) => iso ? `${new Date(iso).toLocaleDateString('en-GB')} ${new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—';

const downloadBlob = async (url, token, filename) => {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const RiskBadge = ({ level }) => (
  <Badge className={level === 'high' ? 'bg-red-600' : level === 'medium' ? 'bg-amber-500' : 'bg-green-600'}>
    {(level || 'low').toUpperCase()} RISK
  </Badge>
);

export const GdprCompliancePage = ({ token, apiCall }) => {
  const [overview, setOverview] = useState(null);
  const [notice, setNotice] = useState(null);
  const [noticeDraft, setNoticeDraft] = useState('');
  const [acks, setAcks] = useState([]);
  const [retention, setRetention] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [dpia, setDpia] = useState([]);
  const [requests, setRequests] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState({ action: '', date_from: '', date_to: '' });
  const [newReq, setNewReq] = useState({ staff_id: '', request_type: 'access', notes: '' });

    const loadAll = useCallback(async () => {
    const results = await Promise.allSettled([
      apiCall('/gdpr/overview', 'GET', null, token),
      apiCall('/privacy-notice', 'GET', null, token),
      apiCall('/privacy-notice/acknowledgements', 'GET', null, token),
      apiCall('/gdpr/retention-settings', 'GET', null, token),
      apiCall('/gdpr/processors', 'GET', null, token),
      apiCall('/gdpr/dpia', 'GET', null, token),
      apiCall('/gdpr/data-requests', 'GET', null, token),
      apiCall('/staff-users', 'GET', null, token),
      apiCall('/gdpr/admins', 'GET', null, token),
    ]);
    const [ov, nt, ak, rt, pr, dp, rq, st, ad] = results.map((r) => (r.status === 'fulfilled' ? r.value : null));
    if (ov) setOverview(ov);
    if (nt) { setNotice(nt); setNoticeDraft(nt.content); }
    if (ak) setAcks(ak);
    if (rt) setRetention(rt.categories || []);
    if (pr) setProcessors(pr);
    if (dp) setDpia(dp);
    if (rq) setRequests(rq);
    if (st) setStaffList(st);
    if (ad) setAdmins(ad);
    if (results.some((r) => r.status === 'rejected')) toast.error('Some GDPR data failed to load');
  }, [apiCall, token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (logFilter.action) params.set('action', logFilter.action);
      if (logFilter.date_from) params.set('date_from', logFilter.date_from);
      if (logFilter.date_to) params.set('date_to', logFilter.date_to);
      params.set('limit', '200');
      const data = await apiCall(`/audit-logs?${params.toString()}`, 'GET', null, token);
      setLogs(data || []);
    } catch {
      toast.error('Failed to load audit logs');
    }
  }, [apiCall, token, logFilter]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  if (!overview) return <p className="text-slate-500 p-6">Loading GDPR compliance data…</p>;

  return (
    <div className="space-y-6" data-testid="gdpr-compliance-page">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <p className="text-sm font-medium text-indigo-900">GDPR readiness controls implemented</p>
        <p className="text-xs text-indigo-700 mt-1">{overview.status_label}</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto bg-white">
          <TabsTrigger value="overview" data-testid="gdpr-overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="notice" data-testid="gdpr-notice-tab">Privacy Notice</TabsTrigger>
          <TabsTrigger value="retention" data-testid="gdpr-retention-tab">Retention</TabsTrigger>
          <TabsTrigger value="processors" data-testid="gdpr-processors-tab">Processors</TabsTrigger>
          <TabsTrigger value="requests" data-testid="gdpr-requests-tab">Data Requests</TabsTrigger>
          <TabsTrigger value="dpia" data-testid="gdpr-dpia-tab">DPIA</TabsTrigger>
          <TabsTrigger value="audit" data-testid="gdpr-audit-tab">Audit Logs</TabsTrigger>
        </TabsList>

        {/* ---------- OVERVIEW ---------- */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Privacy Notice</p><p className="text-xl font-semibold">v{overview.privacy_notice_version}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Staff acknowledged</p><p className="text-xl font-semibold">{overview.acknowledged_count}/{overview.staff_count}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Open data requests</p><p className="text-xl font-semibold">{overview.open_data_requests}{overview.overdue_data_requests > 0 && <span className="text-red-600 text-sm ml-1">({overview.overdue_data_requests} overdue)</span>}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Processors</p><p className="text-xl font-semibold">{overview.processor_count}</p></CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Known limitations & residual risks</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {overview.limitations.map((l) => <p key={l.slice(0, 40)} className="text-sm text-slate-700">⚠️ {l}</p>)}
              {overview.high_risk_processors.length > 0 && (
                <p className="text-sm text-red-700 font-medium">High-risk processors: {overview.high_risk_processors.join(', ')}</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Restricted document access (DBS / ID / health)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {admins.map((a) => (
                <div key={a.id} className="flex items-center justify-between border rounded p-2 text-sm">
                  <span>{a.name} <span className="text-slate-400">({a.email})</span></span>
                  <Button size="sm" variant={a.sensitive_docs_access ? 'default' : 'outline'}
                    data-testid={`sensitive-access-toggle-${a.id}`}
                    onClick={async () => {
                      try {
                        await apiCall(`/gdpr/admins/${a.id}/sensitive-access`, 'PUT', { allowed: !a.sensitive_docs_access }, token);
                        setAdmins(admins.map(x => x.id === a.id ? { ...x, sensitive_docs_access: !a.sensitive_docs_access } : x));
                        toast.success('Access updated');
                      } catch { toast.error('Failed to update'); }
                    }}>
                    {a.sensitive_docs_access ? 'Authorised ✓' : 'Blocked'}
                  </Button>
                </div>
              ))}
              <p className="text-xs text-slate-500">All access to restricted documents is audit-logged. Blocked admins cannot open DBS, identity, right-to-work or health files.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- PRIVACY NOTICE ---------- */}
        <TabsContent value="notice" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                Privacy Notice <Badge variant="secondary">v{notice?.version}</Badge>
                <span className="text-xs font-normal text-slate-500">published {fmtDT(notice?.published_at)} {notice?.published_by && `by ${notice.published_by}`}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea value={noticeDraft} onChange={(e) => setNoticeDraft(e.target.value)} rows={16} className="font-mono text-xs" data-testid="privacy-notice-editor" />
              <div className="flex justify-between items-center">
                <p className="text-xs text-slate-500">Publishing creates a new version. Staff acknowledgements are recorded per version.</p>
                <Button data-testid="publish-notice-btn" onClick={async () => {
                  try {
                    const res = await apiCall('/privacy-notice', 'PUT', { content: noticeDraft }, token);
                    toast.success(`Published version ${res.version}`);
                    loadAll();
                  } catch { toast.error('Failed to publish'); }
                }}>Publish new version</Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Acknowledgements ({acks.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-slate-500 border-b"><th className="py-1">Staff</th><th>Version</th><th>Date/time</th><th>IP</th><th>Context</th></tr></thead>
                  <tbody>
                    {acks.map((a) => (
                      <tr key={`${a.user_id}-${a.version}`} className="border-b border-slate-100">
                        <td className="py-1">{a.user_name}</td><td>v{a.version}</td><td>{fmtDT(a.acknowledged_at)}</td><td>{a.ip_address}</td><td>{a.context}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {acks.length === 0 && <p className="text-slate-500 text-sm py-2">No acknowledgements recorded yet.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- RETENTION ---------- */}
        <TabsContent value="retention" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Retention schedule</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {retention.map((c, i) => (
                <div key={c.key} className="flex items-center gap-3 border rounded p-2 text-sm">
                  <div className="flex-1">
                    <p className="font-medium">{c.label}</p>
                    <p className="text-xs text-slate-500">{c.basis}</p>
                  </div>
                  <Input type="number" min="0" step="0.5" className="w-20" value={c.years} data-testid={`retention-years-${c.key}`}
                    onChange={(e) => setRetention(retention.map((x, j) => j === i ? { ...x, years: parseFloat(e.target.value) || 0 } : x))} />
                  <span className="text-xs text-slate-500">years</span>
                </div>
              ))}
              <div className="flex justify-end">
                <Button data-testid="save-retention-btn" onClick={async () => {
                  try {
                    await apiCall('/gdpr/retention-settings', 'PUT', { categories: retention }, token);
                    toast.success('Retention schedule saved');
                  } catch { toast.error('Failed to save'); }
                }}>Save retention schedule</Button>
              </div>
              <p className="text-xs text-slate-500">Deletion/anonymisation is admin-led via Staff Profiles → Delete Staff. The schedule is enforced through that workflow — records within their retention period are flagged before deletion.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- PROCESSORS ---------- */}
        <TabsContent value="processors" className="space-y-4">
          {processors.map((p, i) => (
            <Card key={p.id || i}>
              <CardContent className="p-4 space-y-1 text-sm">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{p.name}</p>
                  <RiskBadge level={p.risk_level} />
                </div>
                <p><span className="text-slate-500">Purpose:</span> {p.purpose}</p>
                <p><span className="text-slate-500">Data:</span> {p.data_categories}</p>
                <p><span className="text-slate-500">Location:</span> {p.location}</p>
                <p><span className="text-slate-500">Encryption:</span> {p.encryption}</p>
                <p><span className="text-slate-500">DPA status:</span> {p.dpa_status}</p>
                {p.notes && <p className="text-xs text-slate-500 italic">{p.notes}</p>}
              </CardContent>
            </Card>
          ))}
          <p className="text-xs text-slate-500">This register documents where staff data flows. The business must verify DPAs / platform terms with each processor before go-live.</p>
        </TabsContent>

        {/* ---------- DATA REQUESTS ---------- */}
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Log a new data subject request</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2 items-end">
              <div className="min-w-48">
                <Select value={newReq.staff_id} onValueChange={(v) => setNewReq({ ...newReq, staff_id: v })}>
                  <SelectTrigger data-testid="dsr-staff-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                  <SelectContent>
                    {staffList.map((s) => <SelectItem key={s.user.id} value={s.user.id}>{s.user.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Select value={newReq.request_type} onValueChange={(v) => setNewReq({ ...newReq, request_type: v })}>
                  <SelectTrigger data-testid="dsr-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="access">Access / export</SelectItem>
                    <SelectItem value="correction">Correction</SelectItem>
                    <SelectItem value="deletion">Deletion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input className="flex-1 min-w-40" placeholder="Notes (optional)" value={newReq.notes}
                onChange={(e) => setNewReq({ ...newReq, notes: e.target.value })} data-testid="dsr-notes-input" />
              <Button data-testid="create-dsr-btn" onClick={async () => {
                if (!newReq.staff_id) { toast.error('Select a staff member'); return; }
                try {
                  await apiCall('/gdpr/data-requests', 'POST', newReq, token);
                  toast.success('Request logged (due in 1 month)');
                  setNewReq({ staff_id: '', request_type: 'access', notes: '' });
                  loadAll();
                } catch { toast.error('Failed to log request'); }
              }}>Log request</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Requests ({requests.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {requests.length === 0 && <p className="text-sm text-slate-500">No data subject requests logged.</p>}
              {requests.map((r) => {
                const overdue = ['open', 'in_progress'].includes(r.status) && new Date(r.due_date) < new Date();
                return (
                  <div key={r.id} className={`border rounded p-3 text-sm space-y-2 ${overdue ? 'border-red-300 bg-red-50' : ''}`} data-testid={`dsr-row-${r.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{r.staff_name}</span>
                      <Badge variant="secondary" className="capitalize">{r.request_type}</Badge>
                      <Badge className={r.status === 'completed' ? 'bg-green-600' : r.status === 'refused' ? 'bg-slate-500' : 'bg-orange-500'}>{r.status}</Badge>
                      {overdue && <Badge className="bg-red-600">OVERDUE</Badge>}
                      <span className="text-xs text-slate-500">received {fmt(r.received_date)} · due {fmt(r.due_date)}</span>
                    </div>
                    {r.notes && <p className="text-xs text-slate-600">{r.notes}</p>}
                    {r.outcome && <p className="text-xs text-green-700">Outcome: {r.outcome}</p>}
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" data-testid={`dsr-export-${r.id}`} onClick={async () => {
                        try {
                          await downloadBlob(`${BACKEND_URL}/api/gdpr/staff/${r.staff_id}/export`, token, `gdpr-export-${r.staff_name.replace(/\s+/g, '-')}.json`);
                          toast.success('Export downloaded');
                        } catch { toast.error('Export failed'); }
                      }}>⬇ Export data</Button>
                      {['open', 'in_progress'].includes(r.status) && (
                        <>
                          <Button size="sm" variant="outline" onClick={async () => {
                            await apiCall(`/gdpr/data-requests/${r.id}`, 'PUT', { status: 'in_progress' }, token); loadAll();
                          }}>Mark in progress</Button>
                          <Button size="sm" variant="outline" className="text-green-700" data-testid={`dsr-complete-${r.id}`} onClick={async () => {
                            const outcome = window.prompt('Outcome note (e.g. "Export provided", "Anonymised", "Refused — payroll retention"):');
                            if (outcome === null) return;
                            await apiCall(`/gdpr/data-requests/${r.id}`, 'PUT', { status: 'completed', outcome }, token); loadAll();
                          }}>Complete</Button>
                          <Button size="sm" variant="outline" className="text-slate-600" onClick={async () => {
                            const outcome = window.prompt('Reason for refusal (will be recorded):');
                            if (outcome === null) return;
                            await apiCall(`/gdpr/data-requests/${r.id}`, 'PUT', { status: 'refused', outcome }, token); loadAll();
                          }}>Refuse</Button>
                        </>
                      )}
                    </div>
                    {r.request_type === 'deletion' && ['open', 'in_progress'].includes(r.status) && (
                      <p className="text-xs text-slate-500">To action this deletion: go to Staff & Clients → Staff Profiles → Delete Staff, which shows the deletion preview and anonymisation option.</p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- DPIA ---------- */}
        <TabsContent value="dpia" className="space-y-4">
          {dpia.map((d) => (
            <Card key={d.id}>
              <CardContent className="p-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{d.title}</p>
                  <Badge variant="secondary" className="capitalize">{d.status}</Badge>
                </div>
                <p className="text-slate-600">{d.description}</p>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="bg-red-50 rounded p-2"><p className="font-medium text-red-800 mb-1">Risks</p><p className="whitespace-pre-wrap">{d.risks}</p></div>
                  <div className="bg-green-50 rounded p-2"><p className="font-medium text-green-800 mb-1">Mitigations</p><p className="whitespace-pre-wrap">{d.mitigations}</p></div>
                </div>
                <p className="text-xs"><span className="text-slate-500">Residual risk:</span> {d.residual_risk}</p>
                <p className="text-xs text-slate-500">Owner: {d.owner || '—'}</p>
                <Button size="sm" variant="outline" onClick={async () => {
                  const status = window.prompt('Update status (draft / under_review / approved):', d.status);
                  if (!status) return;
                  await apiCall(`/gdpr/dpia/${d.id}`, 'PUT', { ...d, status }, token);
                  loadAll();
                }}>Update status</Button>
              </CardContent>
            </Card>
          ))}
          <p className="text-xs text-slate-500">The DPIA is a living record — the business/DPO should review and approve it before processing real staff data.</p>
        </TabsContent>

        {/* ---------- AUDIT LOGS ---------- */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Audit log search</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 items-end">
                <div className="w-56">
                  <Select value={logFilter.action || 'all'} onValueChange={(v) => setLogFilter({ ...logFilter, action: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      <SelectItem value="view_document">Document views</SelectItem>
                      <SelectItem value="view_restricted_document">Restricted doc views (DBS/ID)</SelectItem>
                      <SelectItem value="upload_document">Document uploads</SelectItem>
                      <SelectItem value="delete_document">Document deletions</SelectItem>
                      <SelectItem value="verify_document">Compliance verifications</SelectItem>
                      <SelectItem value="export_staff_data">Data exports</SelectItem>
                      <SelectItem value="anonymise_staff">Anonymisations</SelectItem>
                      <SelectItem value="archive_staff">Staff archiving</SelectItem>
                      <SelectItem value="sign_document">Document signings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input type="date" className="w-40" value={logFilter.date_from} onChange={(e) => setLogFilter({ ...logFilter, date_from: e.target.value })} data-testid="audit-date-from" />
                <Input type="date" className="w-40" value={logFilter.date_to} onChange={(e) => setLogFilter({ ...logFilter, date_to: e.target.value })} data-testid="audit-date-to" />
                <Button variant="outline" data-testid="export-audit-csv-btn" onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (logFilter.action) params.set('action', logFilter.action);
                    if (logFilter.date_from) params.set('date_from', logFilter.date_from);
                    if (logFilter.date_to) params.set('date_to', logFilter.date_to);
                    await downloadBlob(`${BACKEND_URL}/api/gdpr/audit-logs/export?${params.toString()}`, token, 'audit-logs.csv');
                    toast.success('CSV downloaded');
                  } catch { toast.error('Export failed'); }
                }}>⬇ Export CSV</Button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white"><tr className="text-left text-slate-500 border-b"><th className="py-1">Time</th><th>User</th><th>Action</th><th>Resource</th><th>IP</th></tr></thead>
                  <tbody>
                    {logs.map((l, i) => (
                      <tr key={l.id || `${l.timestamp}-${i}`} className="border-b border-slate-100">
                        <td className="py-1 whitespace-nowrap">{fmtDT(l.timestamp)}</td>
                        <td>{l.user_name}</td>
                        <td><Badge variant="secondary" className="text-[10px]">{l.action}</Badge></td>
                        <td className="max-w-48 truncate" title={l.resource_id}>{l.resource_type}: {l.resource_id}</td>
                        <td>{l.ip_address || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {logs.length === 0 && <p className="text-slate-500 text-sm py-2">No audit entries match the filters.</p>}
              </div>
              <p className="text-xs text-slate-500">Logs record who did what and when — document contents are never stored in logs.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
